/**
 * CampusContext.jsx
 *
 * Single source of truth for campus selection inside a school tenant.
 *
 * - `useCampusContext(tenantId)` — hook that exposes
 *     { campusId, setCampusId, campuses, calendar }
 *   and persists the user's last-picked campus to localStorage per tenant.
 *
 * - `<CampusContextBar tenantId={...} />` — renders a small chip at the top of
 *   every school page showing "Campus · Year · Term · Day N · 🌴 Holiday" with
 *   a quick-switch dropdown.
 *
 * Powered by:
 *   GET /api/school/tenants/{tenantId}/campuses
 *   GET /api/school/tenants/{tenantId}/calendar/current?campus_id=...
 */
import React, { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { authHeaders } from '@/lib/authToken';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { MapPin, CalendarDays, Sun } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fetchJson = async (path) => {
  const res = await fetch(`${API_URL}${path}`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Request failed');
  return res.json();
};

const campusKey = (tenantId) => `school:campus:${tenantId}`;

export const useCampusContext = (tenantId) => {
  const initial = (() => {
    if (!tenantId) return '';
    try { return localStorage.getItem(campusKey(tenantId)) || ''; } catch (_e) { return ''; }
  })();
  const [campusId, _setCampusId] = React.useState(initial);

  const setCampusId = (v) => {
    _setCampusId(v);
    if (!tenantId) return;
    try { v ? localStorage.setItem(campusKey(tenantId), v) : localStorage.removeItem(campusKey(tenantId)); } catch (_e) { /* noop */ }
  };

  const { data: campusesRaw } = useQuery({
    queryKey: ['school-campuses-ctx', tenantId],
    queryFn: () => fetchJson(`/api/school/tenants/${tenantId}/campuses`).catch(() => null),
    enabled: !!tenantId,
  });
  const campuses = useMemo(() => Array.isArray(campusesRaw) ? campusesRaw : (campusesRaw?.items || []), [campusesRaw]);

  // If the persisted campus no longer exists, clear it
  useEffect(() => {
    if (!campusId || campuses.length === 0) return;
    const found = campuses.find(c => c.id === campusId);
    if (!found) setCampusId('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campuses, campusId]);

  const { data: calendar } = useQuery({
    queryKey: ['school-calendar', tenantId, campusId],
    queryFn: () => fetchJson(`/api/school/tenants/${tenantId}/calendar/current?campus_id=${encodeURIComponent(campusId || '')}`),
    enabled: !!tenantId,
    refetchOnWindowFocus: false,
  });

  return { campusId, setCampusId, campuses, calendar };
};

const dayInTerm = (term) => {
  if (!term?.start_date) return null;
  const start = new Date(term.start_date);
  const today = new Date();
  start.setHours(0, 0, 0, 0); today.setHours(0, 0, 0, 0);
  const diff = Math.floor((today - start) / (1000 * 60 * 60 * 24)) + 1;
  return diff > 0 ? diff : null;
};

export const CampusContextBar = ({ tenantId }) => {
  const { campusId, setCampusId, campuses, calendar } = useCampusContext(tenantId);

  // Hide the bar entirely if the school has no campuses configured
  if (!campuses || campuses.length === 0) return null;

  const currentCampus = campuses.find(c => c.id === campusId);
  const ay = calendar?.academic_year;
  const term = calendar?.term;
  const dn = dayInTerm(term);
  const isHoliday = !!calendar?.is_holiday_today;
  const upcoming = (calendar?.upcoming || []).filter(h => h.date !== calendar?.today).slice(0, 1);

  return (
    <div
      className="flex items-center gap-2 flex-wrap rounded-lg border bg-white px-3 py-2 mb-4 text-xs shadow-sm"
      data-testid="campus-context-bar"
    >
      <MapPin className="h-3.5 w-3.5 text-indigo-600" />
      <Select value={campusId || '__shared__'} onValueChange={(v) => setCampusId(v === '__shared__' ? '' : v)}>
        <SelectTrigger className="h-7 w-44 text-xs" data-testid="campus-context-select">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__shared__">All campuses (shared)</SelectItem>
          {campuses.map(c => (
            <SelectItem key={c.id} value={c.id}>{c.name}{c.location ? ` · ${c.location}` : ''}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {currentCampus?.location && (
        <span className="text-slate-500" data-testid="campus-context-location">{currentCampus.location}</span>
      )}
      {ay && (
        <Badge variant="outline" className="text-[10px] gap-1" data-testid="campus-context-ay">
          <CalendarDays className="h-3 w-3" /> {ay.name}
        </Badge>
      )}
      {term && (
        <Badge variant="outline" className="text-[10px]" data-testid="campus-context-term">
          {term.name}{dn ? ` · Day ${dn}` : ''}
        </Badge>
      )}
      {isHoliday && (
        <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-[10px] gap-1" data-testid="campus-context-holiday">
          <Sun className="h-3 w-3" /> Holiday today
        </Badge>
      )}
      {!isHoliday && upcoming.length > 0 && (
        <Badge variant="outline" className="text-[10px] gap-1 border-amber-300 text-amber-700" title={`${upcoming[0].name} on ${upcoming[0].date}`} data-testid="campus-context-next-holiday">
          <Sun className="h-3 w-3" />
          {upcoming[0].name} {(() => {
            const today = new Date(); today.setHours(0, 0, 0, 0);
            const target = new Date(upcoming[0].date); target.setHours(0, 0, 0, 0);
            const dx = Math.round((target - today) / (1000 * 60 * 60 * 24));
            return dx === 0 ? 'today' : (dx === 1 ? 'tomorrow' : `in ${dx}d`);
          })()}
        </Badge>
      )}
      {!ay && !term && (
        <span className="text-slate-400" data-testid="campus-context-empty">
          No current academic year/term set for this campus.
        </span>
      )}
    </div>
  );
};

export default CampusContextBar;


/**
 * <CampusHolidayBanner tenantId={...} /> — small inline alert that surfaces
 * if today is a holiday for the user's currently-picked campus. Mount this at
 * the top of any page that lets staff create lessons / mark attendance /
 * schedule exams, so they don't accidentally schedule on a closed day.
 */
export const CampusHolidayBanner = ({ tenantId, className = '' }) => {
  const { calendar, campuses, campusId } = useCampusContext(tenantId);
  if (!calendar?.is_holiday_today) return null;
  const campus = campuses.find(c => c.id === campusId);
  return (
    <div
      className={`flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 mb-3 ${className}`}
      data-testid="campus-holiday-banner"
    >
      <Sun className="h-4 w-4 shrink-0" />
      <span>
        <b>Today is a public holiday</b>
        {campus ? ` at ${campus.name}` : ''} — attendance, lessons, and exams should not be scheduled.
      </span>
    </div>
  );
};
